import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { fetchPurchasesMonthly, type PriceVarianceFilters, type PurchasesMonthlyRow } from "../../api/reports";
import { toNumber } from "../../lib/format";

const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"] as const;
export const MONTH_INITIALS = MONTHS.map((month) => month[0]);

export function monthLabel(month: string, withYear = true): string {
  const [year, number] = month.split("-");
  const name = MONTHS[Number(number) - 1] ?? month;
  return withYear ? `${name} ${year.slice(2)}` : name;
}

export function monthLongLabel(month: string): string {
  const [year, number] = month.split("-");
  return new Date(Number(year), Number(number) - 1, 1).toLocaleDateString("de-AT", { month: "long", year: "numeric" });
}

export type PurchaseMonth = {
  month: string;
  spend: number;
  lines: number;
  documents: number;
  articles: number;
  overpay: number;
};

export type PurchasesSummary = {
  months: PurchaseMonth[];
  /** The month that is not complete yet: the current one or the last with data. */
  running: PurchaseMonth | null;
  /** Last invoice date in the data (not filtered). */
  dataUntil: string | null;
  total: number;
  average: number;
  lines: number;
  documents: number;
  overpay: number;
  highest: PurchaseMonth | null;
  lowest: PurchaseMonth | null;
};

export function currentMonth(today = new Date()): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * What the Einkaufsvolumen filters on: period, wholesaler, manufacturer. The
 * reference rule only decides which Mehrkosten count, as in the other tabs.
 */
export function purchaseFilters(filters: PriceVarianceFilters) {
  return {
    from: filters.from,
    to: filters.to,
    supplier: filters.supplier,
    manufacturer_id: filters.manufacturer_id,
    reference_quality: filters.reference_quality,
  };
}

function nextMonth(month: string): string {
  const [year, number] = month.split("-").map(Number);
  return number === 12 ? `${year + 1}-01` : `${year}-${String(number + 1).padStart(2, "0")}`;
}

export function lastDayOfMonth(month: string): string {
  const [year, number] = month.split("-").map(Number);
  return `${month}-${String(new Date(year, number, 0).getDate()).padStart(2, "0")}`;
}

/**
 * Ausgaben je Monat; der laufende Monat zählt erst, wenn er abgeschlossen ist.
 * Seitenkopf und Reiter teilen sich diese Abfrage.
 */
export function usePurchasesSummary(filters: PriceVarianceFilters, enabled = true) {
  const scope = purchaseFilters(filters);
  const query = useQuery({
    queryKey: ["reports", "purchases-monthly", scope],
    queryFn: () => fetchPurchasesMonthly(scope),
    enabled,
    staleTime: 60_000,
  });
  const summary = useMemo<PurchasesSummary | null>(() => {
    if (!query.data) return null;
    const now = currentMonth();
    const dataUntil = query.data.data_until ?? null;
    // The last month with data is incomplete unless its last day is in; the
    // current calendar month is never complete.
    const dataMonth = dataUntil ? dataUntil.slice(0, 7) : now;
    const openMonth = dataUntil && dataUntil === lastDayOfMonth(dataMonth) && dataMonth < now ? nextMonth(dataMonth) : dataMonth;
    const byMonth = new Map(query.data.items.map((row: PurchasesMonthlyRow) => [row.month, {
      month: row.month,
      spend: toNumber(row.total_spend) ?? 0,
      lines: row.line_count,
      documents: row.document_count,
      articles: row.article_count,
      overpay: toNumber(row.total_overpay) ?? 0,
    }]));
    // Months without purchases inside the period count as 0, not as missing.
    const first = (scope.from ? scope.from.slice(0, 7) : [...byMonth.keys()].sort()[0]) ?? openMonth;
    // A period that ends earlier (Letztes Jahr, Eigener Zeitraum) stops there.
    const lastInPeriod = scope.to ? scope.to.slice(0, 7) : null;
    const inPeriod = (month: string) => lastInPeriod === null || month <= lastInPeriod;
    const months: PurchaseMonth[] = [];
    for (let month = first; month < openMonth && month < now && inPeriod(month); month = nextMonth(month)) {
      months.push(byMonth.get(month) ?? { month, spend: 0, lines: 0, documents: 0, articles: 0, overpay: 0 });
    }
    const running = inPeriod(openMonth) ? byMonth.get(openMonth) ?? null : null;
    const total = months.reduce((sum, row) => sum + row.spend, 0);
    const sorted = [...months].sort((a, b) => b.spend - a.spend);
    return {
      months,
      running,
      dataUntil,
      total,
      average: months.length ? total / months.length : 0,
      lines: months.reduce((sum, row) => sum + row.lines, 0),
      documents: months.reduce((sum, row) => sum + row.documents, 0),
      overpay: months.reduce((sum, row) => sum + row.overpay, 0),
      highest: sorted[0] ?? null,
      lowest: sorted.length > 1 ? sorted[sorted.length - 1] : null,
    };
  }, [query.data, scope.from, scope.to]);
  return { query, summary };
}

export type ManufacturerSeason = {
  id: string;
  name: string;
  total: number;
  /** Share of the manufacturer's spend per calendar month, in %, all years together. */
  shares: number[];
  /** Same per year, for "Regelmäßig?". */
  years: Record<string, number[]>;
};

const EVEN_SHARE = 100 / 12;

/** Months with at least 1.5 × the even share, strongest first (at most three). */
export function strongMonths(shares: readonly number[]): number[] {
  return shares
    .map((share, index) => ({ share, index }))
    .filter((entry) => entry.share >= EVEN_SHARE * 1.5)
    .sort((a, b) => b.share - a.share)
    .slice(0, 3)
    .map((entry) => entry.index);
}

/** In how many years the strongest month was among that year's three strongest. */
export function regularity(season: ManufacturerSeason, monthIndex: number): { hits: number; years: number } {
  // Only years in which the manufacturer was bought at all.
  const years = Object.values(season.years).filter((shares) => shares.some((share) => share > 0));
  const hits = years.filter((shares) =>
    shares
      .map((share, index) => ({ share, index }))
      .sort((a, b) => b.share - a.share)
      .slice(0, 3)
      .some((entry) => entry.index === monthIndex && entry.share > 0),
  ).length;
  return { hits, years: years.length };
}

export function buildSeasons(rows: readonly PurchasesMonthlyRow[], limit = 10): ManufacturerSeason[] {
  const byManufacturer = new Map<string, { name: string; total: number; months: number[]; years: Map<string, number[]> }>();
  for (const row of rows) {
    if (!row.manufacturer_id) continue;
    const spend = toNumber(row.total_spend) ?? 0;
    const [year, month] = row.month.split("-");
    const index = Number(month) - 1;
    let entry = byManufacturer.get(row.manufacturer_id);
    if (!entry) {
      entry = { name: row.manufacturer_name ?? row.manufacturer_id, total: 0, months: Array(12).fill(0), years: new Map() };
      byManufacturer.set(row.manufacturer_id, entry);
    }
    entry.total += spend;
    entry.months[index] += spend;
    const yearMonths = entry.years.get(year) ?? Array(12).fill(0);
    yearMonths[index] += spend;
    entry.years.set(year, yearMonths);
  }
  return [...byManufacturer.entries()]
    .filter(([, entry]) => entry.total > 0)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, limit)
    .map(([id, entry]) => ({
      id,
      name: entry.name,
      total: entry.total,
      shares: entry.months.map((value) => (value / entry.total) * 100),
      years: Object.fromEntries(
        [...entry.years.entries()].map(([year, months]) => {
          const sum = months.reduce((total, value) => total + value, 0);
          return [year, months.map((value) => (sum > 0 ? (value / sum) * 100 : 0))];
        }),
      ),
    }));
}

/** The last three complete calendar years. */
export function seasonYears(today = new Date()): { from: string; to: string; label: string } {
  const last = today.getFullYear() - 1;
  const first = last - 2;
  return { from: `${first}-01-01`, to: `${last}-12-31`, label: `${first}–${last}` };
}
